import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { useUpdateBundlePrices } from '~/hooks/apis/bundle/useBundles';

import { formatDateWeekDayShortMonth } from '~/services/TimeService';

import { Bundle, BundleComponent, BundlePrice } from '~/types/bundles/bundle';

import { checkInDate } from './hotelStay';
import { TourOption, tourOptionKey, tourOptionPrice, tourOptions } from './tourAvailability';
import useBundleDepartureChecks from './useBundleDepartureChecks';
import useHotelMargins from './useHotelMargins';
import useTourHedgePax from './useTourHedgePax';

interface Props {
  bundle: Bundle;
}

type Part = 'tour' | 'hotel';

const PARTS: ReadonlyArray<Part> = ['tour', 'hotel'];
const PART_LABELS: Record<Part, string> = { tour: 'Tour', hotel: 'Hotel' };
const OCCUPANCIES: ReadonlyArray<{ value: number; label: string; detail: string }> = [
  { value: 2, label: 'Twin share', detail: '2 travellers, one room' },
  { value: 1, label: 'Single', detail: '1 traveller, one room' },
];
const ADJUSTMENT_PATTERN = /^-?\d+$/;
const CHEAPEST = 'cheapest';
const HOTEL_MARGIN_HELP =
  "What we keep after paying the hotel: the package's Salesforce margin (with complimentary addons) plus the margin on date surcharges, after your adjustment. Worked out from the twin share package, so single is a guide only.";

const optionLabel = (option: TourOption) => `${option.tourConfiguration} · ${option.packageConfiguration}`;

const aud = (amount: number | undefined) => (amount === undefined ? '–' : `$${amount.toLocaleString('en-AU')}`);

// Margin bands from the CM checklist: red at 10% or under, green from 19%.
const marginColor = (percentage: number) =>
  percentage <= 10 ? 'error.main' : percentage >= 19 ? 'success.main' : 'warning.main';

const signedAud = (amount: number) => `${amount < 0 ? '−' : '+'}$${Math.abs(amount).toLocaleString('en-AU')}`;

/** The component's price row for one occupancy and departure date. */
function priceRow(component: BundleComponent | undefined, occupancy: number, date: string): BundlePrice | undefined {
  return component?.rows.find((row) => row.occupancy === occupancy)?.prices.find((price) => price.date === date);
}

const plus = (price: number | undefined, adjustment: number) => (price === undefined ? undefined : price + adjustment);

function Stat({ label, value, detail }: { label: string; value: React.ReactNode; detail?: string }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6">{value}</Typography>
        {detail && (
          <Typography variant="caption" color="text.secondary">
            {detail}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function AdjustmentInput({
  label,
  value,
  isChanged,
  onChange,
}: {
  label: string;
  value: string;
  isChanged: boolean;
  onChange: (value: string) => void;
}) {
  const isInvalid = value !== '' && !ADJUSTMENT_PATTERN.test(value);
  return (
    <TextField
      size="small"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={isInvalid}
      inputProps={{ inputMode: 'numeric', 'aria-label': label, style: { textAlign: 'right' } }}
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
      sx={{ width: 104, '& .MuiOutlinedInput-root': isChanged ? { bgcolor: 'action.selected' } : undefined }}
    />
  );
}

function Margin({ amount, sell }: { amount: number; sell: number }) {
  const percentage = sell > 0 ? (amount / sell) * 100 : 0;
  return (
    <Box>
      <Typography variant="body2" fontWeight="medium" color={marginColor(percentage)} noWrap>
        {percentage.toFixed(1)}%
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {amount < 0 ? `−${aud(-amount)}` : aud(amount)}
      </Typography>
    </Box>
  );
}

function Missing({ reason, label = 'Not priced' }: { reason: string; label?: string }) {
  return (
    <Tooltip title={reason}>
      <Typography variant="body2" color="text.secondary" component="span">
        {label}
      </Typography>
    </Tooltip>
  );
}

/** Each departure's live tour and hotel price, the adjustment on each, and what the bundle sells for. */
export default function BundlePricesStep({ bundle }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [occupancy, setOccupancy] = useState(2);
  const [optionKey, setOptionKey] = useState(CHEAPEST);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulk, setBulk] = useState<Record<Part, string>>({ tour: '', hotel: '' });

  const { tour, hotels, departures, tourDepartureOn, hotelResults, blocked, isChecking } =
    useBundleDepartureChecks(bundle);
  const hotel = hotels[0];
  const hotelRates = hotelResults[0]?.data?.[occupancy];
  const updatePrices = useUpdateBundlePrices(bundle.id);
  const hedgePax = useTourHedgePax(tour?.tour_offer_id);
  // In the tour's own order where its configurations are known, else as svc-tour lists them.
  const sequenceOf = (option: TourOption) => {
    const configuration = hedgePax.service?.configurations.find(
      (item) => item.name.toLowerCase() === option.tourConfiguration.toLowerCase(),
    );
    const pkg = configuration?.packages.find(
      (item) => item.name.toLowerCase() === option.packageConfiguration.toLowerCase(),
    );
    return [configuration?.sequenceNumber ?? Infinity, pkg?.sequenceNumber ?? Infinity];
  };
  const options = tourOptions(departures.map(tourDepartureOn).filter(Boolean)).sort((a, b) => {
    const [aTour, aPackage] = sequenceOf(a);
    const [bTour, bPackage] = sequenceOf(b);
    return aTour - bTour || aPackage - bPackage || optionLabel(a).localeCompare(optionLabel(b));
  });
  const selectedOption = options.find((option) => tourOptionKey(option) === optionKey);
  const hotelMargins = useHotelMargins(
    hotel,
    hotel ? departures.map((date) => checkInDate(date, hotel.day_offset ?? 0)) : [],
  );

  const savedPrices = bundle.components.flatMap((component) => component.rows.flatMap((row) => row.prices));
  const valueOf = (price: BundlePrice | undefined) =>
    price ? (drafts[price.id] ?? String(price.price_adjustment)) : '';
  const amountOf = (price: BundlePrice | undefined) => {
    const value = valueOf(price);
    return ADJUSTMENT_PATTERN.test(value) ? Number(value) : 0;
  };
  const isChanged = (price: BundlePrice | undefined) =>
    !!price && price.id in drafts && drafts[price.id] !== String(price.price_adjustment);

  const rows = departures.map((date) => {
    const checkIn = hotel ? checkInDate(date, hotel.day_offset ?? 0) : undefined;
    const prices = { tour: priceRow(tour, occupancy, date), hotel: priceRow(hotel, occupancy, date) };
    const tourDeparture = tourDepartureOn(date);
    const tourPriced = tourOptionPrice(tourDeparture, occupancy, selectedOption);
    const live = {
      tour: tourPriced?.price,
      hotel: checkIn ? (hotelRates?.[checkIn]?.price ?? undefined) : undefined,
    };
    const adjustments = { tour: amountOf(prices.tour), hotel: amountOf(prices.hotel) };
    const sellsFor = { tour: plus(live.tour, adjustments.tour), hotel: plus(live.hotel, adjustments.hotel) };
    const total =
      sellsFor.tour !== undefined && sellsFor.hotel !== undefined ? sellsFor.tour + sellsFor.hotel : undefined;
    const liveHotelMargin = checkIn ? hotelMargins.marginOn(checkIn) : undefined;
    const hotelMargin = liveHotelMargin === undefined ? undefined : liveHotelMargin + adjustments.hotel;
    return {
      date,
      checkIn,
      prices,
      live,
      adjustments,
      sellsFor,
      total,
      hotelMargin,
      tourOption: tourPriced?.option,
      difference: adjustments.tour + adjustments.hotel,
      isBlocked: blocked.includes(date),
    };
  });

  const changed = Object.entries(drafts).filter(([id, value]) => {
    const saved = savedPrices.find((price) => price.id === id);
    return ADJUSTMENT_PATTERN.test(value) && Number(value) !== saved?.price_adjustment;
  });
  const hasInvalid = Object.values(drafts).some((value) => value !== '' && !ADJUSTMENT_PATTERN.test(value));

  const setDraft = (price: BundlePrice | undefined, value: string) => {
    if (price) {
      setDrafts((current) => ({ ...current, [price.id]: value }));
    }
  };

  const applyToAll = () => {
    setDrafts((current) => {
      const next = { ...current };
      PARTS.forEach((part) => {
        if (!ADJUSTMENT_PATTERN.test(bulk[part])) {
          return;
        }
        rows.forEach((row) => {
          const price = row.prices[part];
          if (price) {
            next[price.id] = bulk[part];
          }
        });
      });
      return next;
    });
    setBulk({ tour: '', hotel: '' });
    setIsBulkOpen(false);
  };

  const handleSave = async () => {
    try {
      await updatePrices.mutateAsync({
        prices: changed.map(([id, value]) => ({ id, price_adjustment: Number(value) })),
      });
      // The saved bundle now carries these values, so the drafts are done with.
      setDrafts({});
      enqueueSnackbar('Price adjustments saved', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  if (!tour || !hotel || departures.length === 0) {
    return <Alert severity="info">Add the tour, the hotel and the departures to sell before setting prices.</Alert>;
  }

  const priced = rows.filter((row) => row.total !== undefined);
  const lowest = priced.length > 0 ? Math.min(...priced.map((row) => row.total)) : undefined;
  const adjustedCount = rows.filter((row) => row.difference !== 0).length;
  const hotelMarginPercentages = rows.flatMap((row) =>
    row.hotelMargin !== undefined && row.sellsFor.hotel > 0 ? [(row.hotelMargin / row.sellsFor.hotel) * 100] : [],
  );
  const lowestMargin = hotelMarginPercentages.length > 0 ? Math.min(...hotelMarginPercentages) : undefined;
  // The chosen option's tour configuration, or every one the tour sells when pricing the cheapest.
  const hedgedConfigurations = Array.from(
    new Set((selectedOption ? [selectedOption] : options).map((option) => option.tourConfiguration)),
  ).map((name) => ({ name, pax: hedgePax.hedgePaxOf(name) }));
  const hedgeValues = hedgedConfigurations.flatMap(({ pax }) => (pax === undefined ? [] : [pax]));
  const hedgeLow = hedgeValues.length > 0 ? Math.min(...hedgeValues) : undefined;
  const hedgeHigh = hedgeValues.length > 0 ? Math.max(...hedgeValues) : undefined;
  const hotelMarginMissing = hotelMargins.error
    ? "The hotel's margin couldn't be loaded"
    : !hotelMargins.hasCost && !hotelMargins.isLoading
      ? 'Salesforce has no cost for this package'
      : 'The hotel has no rate for this check-in';
  const selected = OCCUPANCIES.find((option) => option.value === occupancy);
  const perPerson = (amount: number | undefined) =>
    amount === undefined || occupancy === 1 ? undefined : Math.round(amount / occupancy);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={occupancy}
          onChange={(_event, next: number | null) => next && setOccupancy(next)}
        >
          {OCCUPANCIES.map((option) => (
            <ToggleButton key={option.value} value={option.value} sx={{ px: 2, textTransform: 'none' }}>
              <Box textAlign="left">
                <Typography variant="body2" fontWeight="medium">
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {option.detail}
                </Typography>
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          select
          label="Tour option"
          value={selectedOption ? optionKey : CHEAPEST}
          onChange={(event) => setOptionKey(event.target.value)}
          sx={{ minWidth: 280 }}
        >
          <MenuItem value={CHEAPEST}>Cheapest on each departure</MenuItem>
          {options.map((option) => (
            <MenuItem key={tourOptionKey(option)} value={tourOptionKey(option)}>
              {optionLabel(option)}
            </MenuItem>
          ))}
        </TextField>
        <Box flex={1} />
        <Button startIcon={<TuneIcon />} onClick={() => setIsBulkOpen(true)}>
          Adjust every departure
        </Button>
      </Stack>

      <Alert severity="info" variant="outlined">
        <Typography variant="body2">
          <strong>Bundle price = tour + hotel + adjustments</strong>, in AUD for the whole room. A negative adjustment
          is a discount, and applies to {selected?.label.toLowerCase()} on every tour option. The hotel is{' '}
          {hotel.nights} {hotel.nights === 1 ? 'night' : 'nights'} from{' '}
          {(hotel.day_offset ?? 0) === 0 ? 'the day the tour departs' : `day ${(hotel.day_offset ?? 0) + 1}`}.
        </Typography>
      </Alert>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Stat
            label="Bundle from"
            value={aud(lowest)}
            detail={perPerson(lowest) !== undefined ? `${aud(perPerson(lowest))} per person` : 'per room'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Stat
            label="Departures priced"
            value={`${priced.length} of ${rows.length}`}
            detail={
              isChecking
                ? 'Checking live prices…'
                : `${adjustedCount} adjusted · ${blocked.length > 0 ? `${blocked.length} can't be sold` : 'all can be sold'}`
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Stat
            label="Tour hedge pax"
            value={
              hedgeLow === undefined ? '–' : hedgeLow === hedgeHigh ? `${hedgeLow} pax` : `${hedgeLow}–${hedgeHigh} pax`
            }
            detail={
              hedgePax.isLoading
                ? 'Checking the hedge…'
                : hedgePax.error
                  ? "The tour's hedge couldn't be loaded"
                  : hedgeLow === undefined
                    ? 'No hedge set in Salesforce'
                    : hedgedConfigurations.length > 1
                      ? hedgedConfigurations.map(({ name, pax }) => `${name} ${pax ?? '–'}`).join(' · ')
                      : 'Travellers the tour is priced to sell per departure'
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Stat
            label="Lowest hotel margin"
            value={
              lowestMargin === undefined ? (
                '–'
              ) : (
                <Box component="span" color={marginColor(lowestMargin)}>
                  {lowestMargin.toFixed(1)}%
                </Box>
              )
            }
            detail={
              hotelMargins.isLoading
                ? 'Checking margins…'
                : lowestMargin === undefined
                  ? hotelMarginMissing
                  : 'After adjustments'
            }
          />
        </Grid>
      </Grid>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead sx={{ '& th': { whiteSpace: 'nowrap' } }}>
            <TableRow>
              <TableCell rowSpan={2}>Departure</TableCell>
              {PARTS.map((part) => (
                <TableCell
                  key={part}
                  colSpan={part === 'hotel' ? 4 : 3}
                  align="center"
                  sx={{ borderLeft: 1, borderColor: 'divider' }}
                >
                  {PART_LABELS[part]}
                </TableCell>
              ))}
              <TableCell rowSpan={2} align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                Bundle price
              </TableCell>
            </TableRow>
            <TableRow>
              {PARTS.map((part) => (
                <React.Fragment key={part}>
                  <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                    Live price
                  </TableCell>
                  <TableCell align="right">Adjustment</TableCell>
                  <TableCell align="right">Sells for</TableCell>
                  {part === 'hotel' && (
                    <TableCell align="right">
                      <Tooltip title={HOTEL_MARGIN_HELP}>
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                          <span>Margin</span>
                          <InfoOutlinedIcon fontSize="inherit" color="action" />
                        </Stack>
                      </Tooltip>
                    </TableCell>
                  )}
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.date} hover>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight="medium" noWrap>
                      {formatDateWeekDayShortMonth(row.date)}
                    </Typography>
                    {row.isBlocked && (
                      <Tooltip title="Can't be sold: the tour or the hotel can't take a single and a double">
                        <WarningAmberIcon fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                  </Stack>
                  {row.checkIn && row.checkIn !== row.date && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Hotel from {formatDateWeekDayShortMonth(row.checkIn)}
                    </Typography>
                  )}
                </TableCell>
                {PARTS.map((part) => (
                  <React.Fragment key={part}>
                    <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                      {row.live[part] === undefined ? (
                        <Missing
                          reason={
                            part === 'tour'
                              ? 'svc-tour has no AUD price for this departure'
                              : 'The hotel has no rate for this check-in'
                          }
                        />
                      ) : (
                        <>
                          {aud(row.live[part])}
                          {part === 'tour' && !selectedOption && row.tourOption && options.length > 1 && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                              {optionLabel(row.tourOption)}
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.prices[part] ? (
                        <AdjustmentInput
                          label={`${PART_LABELS[part]} adjustment for ${row.date}`}
                          value={valueOf(row.prices[part])}
                          isChanged={isChanged(row.prices[part])}
                          onChange={(value) => setDraft(row.prices[part], value)}
                        />
                      ) : (
                        '–'
                      )}
                    </TableCell>
                    <TableCell align="right">{aud(row.sellsFor[part])}</TableCell>
                    {part === 'hotel' && (
                      <TableCell align="right">
                        {row.hotelMargin !== undefined && row.sellsFor.hotel !== undefined ? (
                          <Margin amount={row.hotelMargin} sell={row.sellsFor.hotel} />
                        ) : hotelMargins.isLoading ? (
                          '…'
                        ) : (
                          <Missing reason={hotelMarginMissing} label="No margin" />
                        )}
                      </TableCell>
                    )}
                  </React.Fragment>
                ))}
                <TableCell align="right" sx={{ borderLeft: 1, borderColor: 'divider' }}>
                  <Typography fontWeight="bold">{aud(row.total)}</Typography>
                  {perPerson(row.total) !== undefined && (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {aud(perPerson(row.total))} pp
                    </Typography>
                  )}
                  {row.difference !== 0 && (
                    <Typography
                      variant="caption"
                      color={row.difference < 0 ? 'success.main' : 'text.secondary'}
                      display="block"
                      noWrap
                    >
                      {row.difference < 0
                        ? `${aud(-row.difference)} off booking separately`
                        : `${signedAud(row.difference)} on booking separately`}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={isBulkOpen} onClose={() => setIsBulkOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Adjust every departure · {selected?.label}</DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            Sets the same adjustment on all {rows.length} departures for {selected?.label.toLowerCase()}. Leave a field
            empty to keep each departure's own. Nothing is saved until you press Save.
          </DialogContentText>
          <Stack spacing={2}>
            {PARTS.map((part) => (
              <TextField
                key={part}
                size="small"
                label={`${PART_LABELS[part]} adjustment`}
                value={bulk[part]}
                onChange={(event) => setBulk((current) => ({ ...current, [part]: event.target.value }))}
                error={bulk[part] !== '' && !ADJUSTMENT_PATTERN.test(bulk[part])}
                helperText="Whole dollars; negative is a discount"
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                fullWidth
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIsBulkOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyToAll}
            disabled={!PARTS.some((part) => ADJUSTMENT_PATTERN.test(bulk[part]))}
          >
            Apply to all
          </Button>
        </DialogActions>
      </Dialog>

      {(changed.length > 0 || hasInvalid) && (
        <Paper
          elevation={6}
          sx={{ position: 'sticky', bottom: 16, px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <Typography flex={1}>
            {hasInvalid
              ? 'Adjustments must be whole dollars, like 50 or -50.'
              : `${changed.length} unsaved ${changed.length === 1 ? 'adjustment' : 'adjustments'}`}
          </Typography>
          <Button onClick={() => setDrafts({})} disabled={updatePrices.isPending}>
            Discard
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={changed.length === 0 || hasInvalid || updatePrices.isPending}
          >
            {updatePrices.isPending ? 'Saving…' : 'Save'}
          </Button>
        </Paper>
      )}
    </Stack>
  );
}
